import { Col, Divider, Form, Input, Modal, ModalProps, notification, Row } from 'antd';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  ConfirmableActionFragment,
  Patientportal_PortalUserMsg,
  useUpdatePasswordMutation,
} from '@/generated/graphql';
import { ActionType } from '@components/confirmable-action/types';
import { ValidateStatus } from 'antd/lib/form/FormItem';
import { Maybe } from 'graphql/jsutils/Maybe';
import { useRouter } from 'next/router';
import { dev } from '../../apollo/ApolloSetup';
import { AveliosPasswordValidation } from '../avelios-password-validation';
import { AveliosButton } from '../common-layout/avelios-button';
import { ConfirmableActionModal } from '../confirmable-action/ConfirmableActionModal';

interface ChangePasswordModalProps extends ModalProps {
  currentUser: Maybe<Patientportal_PortalUserMsg> | undefined;
  currentUserLoading: boolean;
  refetchCurrentUser: () => void;
}

export const ChangePasswordModal: FC<ChangePasswordModalProps> = ({
  currentUser,
  currentUserLoading = false,
  refetchCurrentUser,
  ...props
}) => {
  const { formatMessage } = useIntl();

  const router = useRouter();

  const [updateFailed, setUpdateFailed] = useState<boolean>(false);

  const [oldPassword, setOldPassword] = useState<string>();
  const [newPassword1, setNewPassword1] = useState<string>();
  const [newPassword2, setNewPassword2] = useState<string>();

  const [confirmableAction, setConfirmableAction] = useState<ConfirmableActionFragment | undefined>(
    undefined,
  );
  const [validationStatus, setValidationStatus] = useState<ValidateStatus>();

  const [updatePasswordMutation, { loading: updatePasswordLoading }] = useUpdatePasswordMutation({
    onCompleted: (data) => {
      if (
        data.updatePortalUserPassword?.success === null ||
        data.updatePortalUserPassword?.success === undefined
      ) {
        setUpdateFailed(true);
        return;
      }

      notification.success({
        message: formatMessage({ id: 'change-password.success' }),
        description: formatMessage({
          id: 'change-password.success.description',
        }),
      });

      if (dev && typeof window !== 'undefined') {
        localStorage.removeItem('token');
      }
      refetchCurrentUser();
      router.push('/login');
    },
    onError: () => {
      setUpdateFailed(true);
    },
  });

  const loading = currentUserLoading || updatePasswordLoading;

  return (
    <Modal
      {...props}
      onCancel={(e) => {
        setOldPassword(undefined);
        setNewPassword1(undefined);
        setNewPassword2(undefined);
        setUpdateFailed(false);
        props.onCancel?.(e);
      }}
      footer={
        <AveliosButton
          loading={loading}
          type={'primary'}
          onClick={() =>
            updatePasswordMutation({
              variables: {
                params: {
                  portalUserId: currentUser!.id!,
                  oldPassword: oldPassword,
                  newPassword: newPassword2!,
                },
              },
            })
          }
          disabled={validationStatus !== 'success' || !oldPassword}
        >
          {formatMessage({ id: 'change-password' })}
        </AveliosButton>
      }
      closable={true}
      title={<h2>{formatMessage({ id: 'change-password' })}</h2>}
    >
      <>
        <ConfirmableActionModal
          confirmableAction={confirmableAction}
          confirmAction={(actionConfirmation) => {
            return updatePasswordMutation({
              variables: {
                actionConfirmation: actionConfirmation,
              },
            });
          }}
          confirmActionLoading={updatePasswordLoading}
          onDone={() => {
            router.push('/login');
          }}
          actionType={ActionType.TwoFactorAuthentication}
          onCancel={() => setConfirmableAction(undefined)}
          email={currentUser?.email}
        />
        <Form layout={'vertical'}>
          <Row>
            <Col span={24}>
              <Form.Item
                required
                label={formatMessage({ id: 'change-password.old-password' })}
                name={'old-password'}
                help={updateFailed ? formatMessage({ id: 'change-password.error' }) : undefined}
                validateStatus={updateFailed ? 'error' : undefined}
                hasFeedback={updateFailed}
                rules={[
                  {
                    required: true,
                    message: formatMessage({
                      id: 'change-password.required-field',
                    }),
                  },
                ]}
              >
                <Input.Password
                  allowClear
                  disabled={currentUserLoading || !currentUser?.id}
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  placeholder={formatMessage({
                    id: 'enter-password.placeholder',
                  })}
                />
              </Form.Item>
            </Col>
          </Row>
          <Divider />
          <Row>
            <Col span={24}>
              <Form.Item
                required
                label={formatMessage({ id: 'change-password.new-password' })}
                name={'new-password'}
                validateStatus={validationStatus}
                hasFeedback={!!validationStatus}
                rules={[
                  {
                    required: true,
                    message: formatMessage({
                      id: 'change-password.required-field',
                    }),
                  },
                ]}
              >
                <Input.Password
                  allowClear
                  disabled={currentUserLoading || !currentUser?.id}
                  value={newPassword1}
                  onChange={(e) => setNewPassword1(e.target.value)}
                  placeholder={formatMessage({
                    id: 'enter-password.placeholder',
                  })}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Form.Item
                required
                label={formatMessage({
                  id: 'change-password.new-password.repeat',
                })}
                name={'repeat-password'}
                validateStatus={validationStatus}
                hasFeedback={!!validationStatus}
                rules={[
                  {
                    required: true,
                    message: formatMessage({
                      id: 'change-password.required-field',
                    }),
                  },
                ]}
              >
                <Input.Password
                  allowClear
                  disabled={currentUserLoading || !currentUser?.id}
                  value={newPassword2}
                  onChange={(e) => setNewPassword2(e.target.value)}
                  placeholder={formatMessage({
                    id: 'enter-password.placeholder',
                  })}
                />
              </Form.Item>
            </Col>
          </Row>
          <AveliosPasswordValidation
            password1={newPassword1 || ''}
            password2={newPassword2 || ''}
            onChange={setValidationStatus}
          />
        </Form>
      </>
    </Modal>
  );
};
